/**
 * @file `<AdminTerminalView/>` — read-only xterm.js mirror of a running
 *       Machine's terminal, for the Hypervisors panel detail drawer.
 * @module @labcraft/admin/ui/hypervisor/terminal-view
 *
 * Unlike the learner's `<TerminalView/>` (which fits xterm to its
 * container and drives the pty winsize from the viewport), the admin
 * mirror is inverted: it is **read-only** and **learner-sized**. The
 * operator observes but never types, and the terminal renders at the
 * learner's own cols/rows — pushed down as `resize` frames — so the
 * admin sees exactly what the learner sees. The container is
 * `fit-content`, so the drawer grows/shrinks to the mirrored terminal's
 * size whenever the learner resizes.
 *
 * Wiring:
 *   driver.onData(bytes)         → xterm.write(bytes)   (pty → display)
 *   driver.onResize(cols, rows)  → xterm.resize(...)    (mirror winsize)
 *
 * No `xterm.onData` bridge (stdin disabled) and no fit-addon — the size
 * is dictated by the learner, not the pane.
 */

import { useEffect, useRef, type JSX } from 'react'
import { Terminal as XTerm } from '@xterm/xterm'
import '@xterm/xterm/css/xterm.css'

/**
 * Minimal read-only driver shape `<AdminTerminalView/>` consumes. The
 * client's `AdminTerminal` satisfies it; a fake satisfies it in unit
 * tests. Receive-only — no `write` / input surface.
 */
export interface AdminTerminalDriver {
  /** Open the underlying subscription. Called once on mount. */
  attach(): void
  /** Subscribe to pty stdout bytes (server → client). */
  onData(handler: (bytes: Uint8Array) => void): () => void
  /** Subscribe to the mirrored learner winsize (cols, rows). */
  onResize(handler: (cols: number, rows: number) => void): () => void
  /** Subscribe to connection-status changes. */
  onStatus(handler: (status: string) => void): () => void
  /** Tear the subscription down. Called on unmount. */
  close(): void
}

export type AdminTerminalViewProps = {
  /** The read-only observer driver for the selected machine. */
  terminal: AdminTerminalDriver
  /**
   * Notified whenever the mirrored winsize changes, so the parent can
   * render a `cols×rows` label. Optional.
   */
  onDimensions?: (cols: number, rows: number) => void
}

/** Render a read-only, learner-sized terminal mirror. */
export function AdminTerminalView({
  terminal,
  onDimensions,
}: AdminTerminalViewProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const xterm = new XTerm({
      fontFamily:
        "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 13,
      lineHeight: 1.5,
      // Read-only observer: no cursor blink, stdin disabled so the
      // operator can't type into the learner's live session.
      cursorBlink: false,
      cursorStyle: 'underline',
      disableStdin: true,
      theme: {
        background: '#0F0D2A',
        foreground: '#c3bfe6',
        cursor: '#AFA9EC',
        cursorAccent: '#0F0D2A',
        selectionBackground: 'rgba(83, 74, 183, 0.4)',
        black: '#0B0920',
        red: '#d86474',
        green: '#6FE2BD',
        yellow: '#e6c889',
        blue: '#7F77DD',
        magenta: '#AFA9EC',
        cyan: '#9bdce0',
        white: '#c3bfe6',
        brightBlack: '#3a3475',
        brightRed: '#ec8896',
        brightGreen: '#8fe8c9',
        brightYellow: '#f1d8a3',
        brightBlue: '#AFA9EC',
        brightMagenta: '#CECBF6',
        brightCyan: '#bce5e8',
        brightWhite: '#EEEDFE',
      },
      allowProposedApi: true,
    })
    xterm.open(container)

    // pty stdout → display.
    const offData = terminal.onData(bytes => {
      xterm.write(bytes)
    })

    // Mirror the learner's winsize. Resizing xterm sizes its own DOM,
    // and the `fit-content` container lets the drawer grow to match.
    const offResize = terminal.onResize((cols, rows) => {
      try {
        xterm.resize(cols, rows)
        onDimensions?.(cols, rows)
      } catch {
        /* defensive — never let a mirror resize crash the panel */
      }
    })

    // Status is observed for completeness (and so a future reconnect
    // banner can hang off it); no resize-refire is needed since the
    // hypervisor pushes the current winsize on attach.
    const offStatus = terminal.onStatus(() => {})

    terminal.attach()

    return () => {
      offData()
      offResize()
      offStatus()
      terminal.close()
      xterm.dispose()
    }
  }, [terminal, onDimensions])

  return <div ref={containerRef} className="hv-term" />
}
